<template>
  <validated-form
    v-slot="{ dirty }"
    ref="configureTieredDeliveryFees"
    form-id="configureTieredDeliveryFees"
    @valid-submit="handleSubmit"
  >
    <modal-card title="Configure Delivery Fee Tiers">
      <b-loading v-if="isFetching" active :is-full-page="false" />

      <div
        v-if="!isFetching"
        class="dist-y"
      >
        <div
          v-for="(tier, index) in tiersForm"
          :key="`tier-${index}`"
          class="is-flex align-start gap"
        >
          <validated-text-input
            :key="`minAmount${index}`"
            v-model="tier.tierMinAmount"
            type="dollars"
            name="tierMinAmount"
            label="Min Amount"
            :hide-label="index > 0"
            :placeholder="index === 0 ? 'Amount' : ''"
            :rules="{
              required: true,
              min_value: 0.01,
              lessThan: { target: tier.tierMaxAmount }
            }"
            class="mar-none tier-input"
            :disabled="index > 0"
          />
          <validated-text-input
            :key="`maxAmount${index}`"
            v-model="tier.tierMaxAmount"
            type="dollars"
            name="tierMaxAmount"
            label="Max Amount"
            :hide-label="index > 0"
            placeholder="Amount"
            :rules="{
              required: true,
              min_value: 0.01,
              greaterThan: { target: tier.tierMinAmount }
            }"
            class="mar-none tier-input"
            @blur="handleMaxAmountBlur(index)"
          />
          <validated-input
            class="dropdown-field mar-none"
            name="feeType"
            label="Fee Type"
            :hide-label="index > 0"
            rules="required"
          >
            <b-select
              v-model="tier.feeTypeId"
              placeholder="Select type"
              class="is-inline-block"
              expanded
              @input="handleFeeTypeChange($event, index)"
            >
              <option :value="null" disabled>-</option>
              <option
                :key="feeTypesById.FLAT_FEE"
                :value="feeTypesById.FLAT_FEE"
              >
                Flat Fee
              </option>
              <option
                :key="feeTypesById.PERCENTAGE_DISCOUNT"
                :value="feeTypesById.PERCENTAGE_DISCOUNT"
              >
                Percentage
              </option>
            </b-select>
          </validated-input>
          <validated-text-input
            v-model="tier.tierFeeAmount"
            :type="tier.feeTypeId === feeTypesById.PERCENTAGE_DISCOUNT ? 'float' : 'dollars'"
            label="Fee Amount"
            :name="`feeAmount${index}`"
            :hide-label="index > 0"
            :rules="tier.feeTypeId === feeTypesById.PERCENTAGE_DISCOUNT ? { between: [0.01, 100], required: true } : { min_value: 0.01, required: true }"
            class="mar-none flex-grow"
            :placeholder="`Fee ${tier.feeTypeId === feeTypesById.PERCENTAGE_DISCOUNT ? 'Percentage' : 'Amount'}`"
          />
          <b-button
            v-if="tiersForm.length > 2"
            style="height: unset"
            type="is-danger is-light"
            :class="{ 'delete-icon-offset': index === 0 }"
            @click="removeTier(index)"
          >
            <b-icon icon="trash-alt" />
          </b-button>
        </div>
        <template>
          <b-button
            type="is-primary is-light"
            icon-left="plus"
            @click="addTier"
          >
            Add Tier
          </b-button>
          <b-button
            type="is-danger is-light"
            icon-left="trash-alt"
            class="mar-l-md"
            @click="clearTiers"
          >
            Clear All Tiers
          </b-button>
        </template>
      </div>
      <template #footer>
        <div class="buttons all-bold">
          <b-button rounded @click="$_confirmCloseModal({ programmatic: true })">
            Close
          </b-button>
          <b-button
            v-tabbable
            rounded
            :loading="isUpdating"
            native-type="submit"
            type="is-primary"
            :class="['is-bold', dirty && 'pulse-slow']"
          >
            Save
          </b-button>
        </div>
      </template>
    </modal-card>
  </validated-form>
</template>

<script>
  import confirmModalCloseMixin from '@/mixins/confirm-modal-close';

  import FeeType from '@/store/classes/FeeType';
  import MerchantDeliveryTier from '@/store/classes/MerchantDeliveryTier';
  import StoreDeliveryTier from '@/store/classes/StoreDeliveryTier';

  export default {
    name: 'ConfigureTieredDeliveryFeesModal',

    mixins: [confirmModalCloseMixin],

    props: {
      entityType: {
        type: String,
        required: true,
        validator: value => ['merchant', 'store'].includes(value)
      },
      deliveryServiceId: {
        type: Number,
        required: true
      },
      platformTypeId: {
        type: Number,
        required: true
      },
      entityId: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        tiersForm: [],
        feeTypesById: FeeType.typesById()
      };
    },

    computed: {
      isFetching() {
        return MerchantDeliveryTier.$state().fetching || StoreDeliveryTier.$state().fetching;
      },

      isUpdating() {
        return MerchantDeliveryTier.$state().updating || StoreDeliveryTier.$state().updating;
      },

      isMerchantEntityType() {
        return this.entityType === 'merchant';
      },

      entityClass() {
        return this.isMerchantEntityType ? MerchantDeliveryTier : StoreDeliveryTier;
      }
    },

    async created() {
      await this.onCreated();
    },

    methods: {
      async onCreated() {
        if (this.isMerchantEntityType) {
          await this.fetchMerchantTiers();
        }
        else {
          await this.fetchStoreTiers();
        }

        const tiers = this.entityClass
          .query()
          .where('deliveryServiceId', this.deliveryServiceId)
          .where('platformTypeId', this.platformTypeId)
          .get();

        if (!tiers.length) {
          this.initializeTiers();
        }
        else {
          this.tiersForm = this.$clone(tiers);
        }
      },

      initializeTiers() {
        // Initialize with two empty tiers
        this.tiersForm = [
          this.createEmptyTier(1),
          this.createEmptyTier(2)
        ];
      },

      createEmptyTier(tierLevel) {
        return new this.entityClass({
          tierLevel,
          tierMinAmount: tierLevel === 1 ? '0.00' : this.calculateNextMinAmount(),
          tierMaxAmount: null,
          tierFeeAmount: null,
          feeTypeId: this.feeTypesById.FLAT_FEE,
          deliveryServiceId: this.deliveryServiceId,
          platformTypeId: this.platformTypeId,
          [this.isMerchantEntityType ? 'merchantId' : 'storeId']: this.entityId
        });
      },

      calculateNextMinAmount() {
        if (!this.tiersForm || !this.tiersForm.length) return null;

        const lastTier = this.tiersForm[this.tiersForm.length - 1];
        if (!lastTier || !lastTier.tierMaxAmount) return null;

        // Add one penny to the previous tier's max amount
        return (parseFloat(lastTier.tierMaxAmount) + 0.01).toFixed(2);
      },

      addTier() {
        const newTierLevel = this.tiersForm.length + 1;
        const newTier = this.createEmptyTier(newTierLevel);

        this.tiersForm.push(newTier);
      },

      removeTier(index) {
        this.tiersForm = this.tiersForm.filter((_, i) => i !== index);

        // Recalculate tier levels
        this.tiersForm.forEach((tier, i) => {
          tier.tierLevel = i + 1;

          // Recalculate min amounts for all tiers except the first
          if (i > 0) {
            tier.tierMinAmount = (parseFloat(this.tiersForm[i - 1].tierMaxAmount) + 0.01).toFixed(2);
          }
        });
      },

      clearTiers() {
        this.tiersForm = [];
        this.initializeTiers();
      },

      handleFeeTypeChange(value, index) {
        this.tiersForm[index].tierFeeAmount = null;
      },

      handleMaxAmountBlur(index) {
        if (index < this.tiersForm.length - 1 && this.tiersForm[index].tierMaxAmount) {
          this.tiersForm[index + 1].tierMinAmount = (parseFloat(this.tiersForm[index].tierMaxAmount) + 0.01).toFixed(2);
        }
      },

      async handleSubmit() {
        try {
          if (this.isMerchantEntityType) {
            await this.saveMerchantTiers(this.tiersForm);
          }
          else {
            await this.saveStoreTiers({ storeId: this.entityId, tiers: this.tiersForm });
          }

          this.$_onRequestSuccess({
            toastOptions: { message: 'Successfully created your delivery fee tiers!' },
            options: {
              closeParent: true
            }
          });
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was a problem saving your delivery fee tiers.' },
            error
          });
        }
      },

      async fetchMerchantTiers() {
        try {
          await MerchantDeliveryTier.fetchTiers();
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was a problem fetching the merchant delivery fee tiers.' },
            error
          });
        }
      },

      async saveMerchantTiers() {
        try {
          await MerchantDeliveryTier.bulkReplaceTiers(this.tiersForm);
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was a problem saving your merchant delivery fee tiers.' },
            error
          });
        }
      },

      async fetchStoreTiers() {
        try {
          await StoreDeliveryTier.fetchTiers(this.entityId);
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was a problem fetching the store delivery fee tiers.' },
            error
          });
        }
      },

      async saveStoreTiers() {
        try {
          await StoreDeliveryTier.bulkReplaceTiers({
            storeId: this.entityId,
            tiers: this.tiersForm
          });
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was a problem saving your merchant delivery fee tiers.' },
            error
          });
        }
      }
    }
  };
</script>

<style lang="sass" scoped>
  .tier-input
    width: 120px

  .delete-icon-offset
    margin-top: 2rem
</style>
